/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 09/11/21
 */

import React from 'react';
import {
    ArrayControlProps,
    ControlElement,
    isObjectArrayControl,
    isPrimitiveArrayControl,
    or,
    RankedTester,
    rankWith,
} from '@jsonforms/core';
import { Table, Tbody, Thead } from '@chakra-ui/react';
import ChakraTableToolbar from './ChakraTableToolbar';
import ChakraTableRows from './ChakraTableRows';
import { getValidColumnProps } from './utils';
import ChakraTableHeaderCell from './ChakraTableHeaderCell';
import configuredAreEqual from '../../utils/configuredAreEqual';
import startCase from 'lodash/startCase';
import omit from 'lodash/omit';
import { withNamedJsonFormsArrayControlProps } from '@/forms-renderers/chakra-ui/utils/withNamedJsonFormsHOC';
import { namedMemo } from '@/utils/namedMemo';

export const ChakraTableControl = namedMemo(
    'ChakraTableControl',
    (props: ArrayControlProps) => {
        const { label, enabled, uischema, schema, rootSchema, path, addItem } = props;
        const controlElement = uischema as ControlElement;
        const isObjectSchema = schema.type === 'object';
        const headerCells = isObjectSchema
            ? getValidColumnProps(schema).map((prop) => (
                  <ChakraTableHeaderCell key={prop} title={schema.properties?.[prop]?.title ?? startCase(prop)} />
              ))
            : undefined;

        const rowsProps = omit(props, 'data');

        return (
            <Table border="1px solid" borderColor="gray.200">
                <Thead>
                    <ChakraTableToolbar
                        label={label}
                        addItem={addItem}
                        numColumns={
                            isObjectSchema && headerCells && Array.isArray(headerCells) ? headerCells.length : 1
                        }
                        path={path}
                        uischema={controlElement}
                        schema={schema}
                        rootSchema={rootSchema}
                        enabled={enabled}
                    />
                    {/* {isObjectSchema && (
                        <Tr>
                            {headerCells}
                            {enabled ? <Td /> : null}
                        </Tr>
                    )} */}
                </Thead>
                <Tbody>
                    <ChakraTableRows {...rowsProps} />
                </Tbody>
            </Table>
        );
    },
    configuredAreEqual({
        ignore: ['data', 'childErrors', 'addItem', 'removeItems', 'moveUp', 'moveDown'],
    }),
);

export const chakraTableControlTester: RankedTester = rankWith(3, or(isObjectArrayControl, isPrimitiveArrayControl));
export default withNamedJsonFormsArrayControlProps(ChakraTableControl);
